import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getProviders, getCategories } from "../../../../utility/getMarketplaceData.js";
import CategoryFilter from "../../../../components/CategoryFilter/CategoryFilter";
import ProviderCard from "../../../../components/marketplace/ProviderCard";
import Pagination from "../../../../components/Pagination/Pagination";
import LoadingSpinner from "../../../../components/LoadingSpinner.js";
import CartButton from "../../../../components/marketplace/CartButton/CartButton";
import "./Providers.scss";

import { ReactComponent as All } from "../../../../images/All.svg";
import { ReactComponent as WC } from "../../../../images/toilet.svg";
import { ReactComponent as Services } from "../../../../images/insurance.svg";
import { ReactComponent as Drinks } from "../../../../images/Beers.svg";
import { ReactComponent as Cups } from "../../../../images/cup.svg";
import { ReactComponent as Equipments } from "../../../../images/computer.svg";

const Providers = () => {
  const { t } = useTranslation("marketplace");
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [providers, setProviders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const itemsPerPage = 12;

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [providersData, categoriesData] = await Promise.all([
          getProviders(),
          getCategories(),
        ]);

        setProviders(providersData);
        setCategories(categoriesData);
      } catch (error) {
        console.error("Error loading data:", error);
        setProviders([]);
        setCategories([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  const getCategoryIcon = (categoryName) => {
    const iconMap = {
      Toilettes: WC,
      Services: Services,
      Boissons: Drinks,
      Gobelets: Cups,
      Matériels: Equipments,
    };
    return iconMap[categoryName] || Services;
  };

  const dynamicFilters = [
    { id: "All", label: t("categories.all"), icon: All },
    ...categories.map((category) => {
      return {
        id: category.name,
        label: t(`category_names.${category.name}`, { defaultValue: category.name }),
        icon: getCategoryIcon(category.name),
      };
    }),
  ];

  const filteredProviders =
    activeCategory === "All"
      ? providers
      : providers.filter((provider) => provider.categoryId === activeCategory);

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const searchedProviders = normalizedSearch
    ? filteredProviders.filter((provider) => {
        const searchable = [
          provider.name,
          provider.description,
          provider.fullDescription,
          provider.categoryId,
          provider.categoryName,
          ...(Array.isArray(provider.badges) ? provider.badges : []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return searchable.includes(normalizedSearch);
      })
    : filteredProviders;

  const totalPages = Math.ceil(searchedProviders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentProviders = searchedProviders.slice(startIndex, startIndex + itemsPerPage);

  const getCategoryLabel = (categoryId) => {
    if (!categoryId) return "";
    return t(`category_names.${categoryId}`, { defaultValue: categoryId });
  };

  const handleCategorySelect = (categoryId) => {
    setActiveCategory(categoryId);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleProviderClick = (providerId) => {
    navigate(`/dashboard/marketplace/${providerId}`);
  };

  const resetFilters = () => {
    setActiveCategory("All");
    setSearchQuery("");
    setCurrentPage(1);
  };

  return (
    <div className="container-fluid marketplace">
      <div className="card themeCard">
        <div className="card-body p-sm-3">
          <div className="marketplace__hero">
            <div className="marketplace__hero-content">
              <span className="marketplace__eyebrow">
                {t("home.eyebrow", { defaultValue: "Marketplace Comitty" })}
              </span>
              <h3>{t("home.title", { defaultValue: "Trouvez vos fournisseurs événementiels" })}</h3>
              <p>
                {t("home.subtitle", {
                  defaultValue:
                    "Découvrez des partenaires pour vos boissons, équipements, services et demandes sur mesure.",
                })}
              </p>
            </div>
            <CartButton />
          </div>

          <div className="marketplace__toolbar">
            <div className="marketplace__search">
              <label htmlFor="marketplace-provider-search">
                {t("home.search_label", { defaultValue: "Rechercher" })}
              </label>
              <input
                id="marketplace-provider-search"
                type="search"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder={t("home.search_placeholder", {
                  defaultValue: "Nom, service, catégorie...",
                })}
              />
            </div>
            <div className="marketplace__stats">
              <strong>{searchedProviders.length}</strong>
              <span>
                {t("home.providers_count", {
                  count: searchedProviders.length,
                  defaultValue: "fournisseur(s)",
                })}
              </span>
            </div>
          </div>

          <div className="marketplace__filters">
            <CategoryFilter
              filters={dynamicFilters}
              activeFilter={activeCategory}
              onFilterChange={handleCategorySelect}
            />
          </div>

          <div className="providers-grid">
            {isLoading ? (
              <div className="providers-grid__loading">
                <LoadingSpinner width="50px" />
              </div>
            ) : currentProviders.length > 0 ? (
              currentProviders.map((provider) => (
                <ProviderCard
                  key={provider.id}
                  provider={provider}
                  categoryLabel={getCategoryLabel(provider.categoryId)}
                  onClick={() => handleProviderClick(provider.id)}
                />
              ))
            ) : (
              <div className="marketplace__empty">
                <h4>{t("no_results")}</h4>
                <p>
                  {t("home.empty_hint", {
                    defaultValue:
                      "Essayez une autre recherche ou réinitialisez les filtres pour revoir tous les fournisseurs.",
                  })}
                </p>
                <button type="button" onClick={resetFilters}>
                  {t("home.reset_filters", { defaultValue: "Réinitialiser les filtres" })}
                </button>
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="pagination-wrapper">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Providers;
